// src/Services/autoLogin.js

/**
 * Decode a base64-encoded JSON payload from the `auth` query parameter.
 * The expected object shape is:
 * {
 *   route: { name: string, params?: object, query?: object },
 *   auth: { email: string, password: string },
 *   storeDB?: string
 * }
 * @param {string} b64 Value of the `auth` query (may be URL-encoded)
 * @returns {{route?: object, auth?: {email:string,password:string}, storeDB?: string}|null}
 */
import { idbTransactionalStorage } from "@/Services/idbStorage";

export function decodeAuthPayload(b64) {
  if (!b64) return null;
  try {
    const json = atob(decodeURIComponent(b64));
    const obj = JSON.parse(json);
    if (!obj || typeof obj !== 'object') return null;
    if (!obj.auth || !obj.auth.email || !obj.auth.password) return null;
    if (!obj.route || !obj.route.name) return null;
    return obj;
  } catch (e) {
    console.warn('[autoLogin] Invalid base64 payload:', e);
    return null;
  }
}

/**
 * Extract `auth` payload from the current location's query string and decode it.
 * @returns {ReturnType<typeof decodeAuthPayload>} decoded payload or null
 */
export function extractAuthPayloadFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    const b64 = params.get('auth');
    return decodeAuthPayload(b64);
  } catch (e) {
    console.warn('[autoLogin] Failed to extract from location:', e);
    return null;
  }
}

/**
 * Remove persisted store data so a deep-link auth payload always starts
 * from a clean slate. Clears both localStorage and the Vuex IDB record.
 */
export async function clearPersistedAuthState() {
  try {
    localStorage.clear();
  } catch (e) {
    console.warn('[autoLogin] Failed to clear localStorage:', e);
  }

  try {
    await idbTransactionalStorage.removeItem('vuex');
  } catch (e) {
    console.warn('[autoLogin] Failed to clear persisted Vuex state:', e);
  }
}
